import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Loader2, Calendar, TrendingUp, PoundSterling } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getRoleService } from '@/services/roleService';
import { useDataSource } from '@/contexts/DataSourceContext';
import type { RoleRateHistory } from '@/types/role';
import { AddRateDialog } from './AddRateDialog';
import { formatCurrency, formatPercentage, formatDate } from '@/utils/formatters';
import { toast } from 'sonner';

interface RateHistoryTabProps {
  roleId: number;
  onRateAdded: () => void;
}

export const RateHistoryTab: React.FC<RateHistoryTabProps> = ({
  roleId,
  onRateAdded,
}) => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('roles-rates');
  const roleService = useMemo(() => getRoleService(screenMode), [screenMode]);
  
  const [rateHistory, setRateHistory] = useState<RoleRateHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const loadRateHistory = async () => {
    setIsLoading(true);
    try {
      const data = await roleService.getRateHistory(roleId);
      setRateHistory(data);
    } catch (err) {
      console.error('Error loading rate history:', err);
      toast.error('Failed to load rate history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRateHistory();
  }, [roleId]);

  const handleRateAdded = () => {
    setIsAddDialogOpen(false);
    loadRateHistory();
    onRateAdded();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Rate History</h3>
          <p className="text-sm text-muted-foreground">
            All rate changes for this role, ordered by effective date
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Rate
        </Button>
      </div>

      {rateHistory.length === 0 ? (
        <div className="text-center py-8 border rounded-lg border-dashed">
          <PoundSterling className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-4">No rates defined yet</p>
          <Button onClick={() => setIsAddDialogOpen(true)} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add First Rate
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {rateHistory.map((rate, index) => (
            <div
              key={rate.id}
              className={`p-4 border rounded-lg ${
                rate.isCurrent
                  ? 'border-primary bg-primary/5'
                  : rate.isFuture
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                  : 'border-muted'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">
                      {formatDate(rate.effectiveFrom)}
                      {rate.effectiveTo && ` — ${formatDate(rate.effectiveTo)}`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Added {formatDate(rate.createdAt)}
                      {rate.createdByName && ` by ${rate.createdByName}`}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {rate.isCurrent && (
                    <Badge variant="default">Current</Badge>
                  )}
                  {rate.isFuture && (
                    <Badge variant="secondary">Future</Badge>
                  )}
                  {!rate.isCurrent && !rate.isFuture && (
                    <Badge variant="outline">Historical</Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Billing Rate</div>
                  <div className="font-mono font-semibold">
                    {rate.billingRate
                      ? `${formatCurrency(rate.billingRate, rate.currency)}/hr`
                      : '—'}
                  </div>
                  {rate.billingRate && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatCurrency(rate.billingRate * 8, rate.currency)}/day
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Cost Rate</div>
                  <div className="font-mono font-semibold">
                    {rate.costRate
                      ? `${formatCurrency(rate.costRate, rate.currency)}/hr`
                      : '—'}
                  </div>
                  {rate.costRate && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatCurrency(rate.costRate * 8, rate.currency)}/day
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Margin</div>
                  <div className="flex items-center gap-1">
                    {rate.marginPercentage !== null && rate.marginPercentage !== undefined ? (
                      <>
                        <TrendingUp className={`h-4 w-4 ${rate.marginPercentage > 0 ? 'text-green-600' : 'text-red-600'}`} />
                        <span className={`font-semibold ${rate.marginPercentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPercentage(rate.marginPercentage)}
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Show rate change indicator */}
              {index < rateHistory.length - 1 && (
                <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                  {(() => {
                    const prevRate = rateHistory[index + 1];
                    const changes: string[] = [];
                    
                    if (rate.billingRate !== prevRate.billingRate) {
                      const diff = rate.billingRate && prevRate.billingRate 
                        ? rate.billingRate - prevRate.billingRate 
                        : 0;
                      changes.push(`Billing ${diff > 0 ? '↑' : '↓'} ${formatCurrency(Math.abs(diff), rate.currency)}`);
                    }
                    
                    if (rate.costRate !== prevRate.costRate) {
                      const diff = rate.costRate && prevRate.costRate 
                        ? rate.costRate - prevRate.costRate 
                        : 0;
                      changes.push(`Cost ${diff > 0 ? '↑' : '↓'} ${formatCurrency(Math.abs(diff), rate.currency)}`);
                    }
                    
                    return changes.length > 0 ? `Changes: ${changes.join(', ')}` : null;
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AddRateDialog
        roleId={roleId}
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSuccess={handleRateAdded}
      />
    </div>
  );
};
